/**
 * CORE-747 — where «Назад» goes from a Live-session surface.
 *
 * Every Live surface carried the same shape:
 *
 *     navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Home')
 *
 * The fallback fires only when there is no in-app history — which is exactly
 * the case of a visitor who arrived by an external direct link, and that is
 * how guests reach a Live-практика in the first place (the host shares the
 * link in a chat). So the branch that never mattered for the team was the
 * only branch a guest ever hit: «Назад» dropped them on `/` — or, from inside
 * the room, on the personal Dashboard — neither of which lists the session
 * they were just looking at. Getting back meant finding the link again.
 *
 * A guest now lands on the Live calendar instead, focused on the session's own
 * day. What makes that a real answer rather than another empty page is
 * CORE-748: the calendar only started sourcing rooms from the platform-wide
 * public list then, so before it `/lives` would have been just as barren as
 * `/`. Signed-in visitors keep whatever destination their screen already
 * chose — they are not what this bug is about, and their fallbacks (Dashboard
 * «События», the archive tab) are personal surfaces a guest has no business
 * landing on.
 *
 * Pure, no RN imports, so it runs in the node jest env — same reason
 * `guestEntry.ts` and `calendarRooms.ts` live here rather than beside the
 * screens that use them.
 */
import { dateToISO } from './calendarSpan';

/** The slice of a React Navigation object these helpers touch. Deliberately
 *  loose on `navigate` — the real one is heavily overloaded per route. */
export interface BackNavigator {
  canGoBack: () => boolean;
  goBack: () => void;
  navigate: (...args: any[]) => void;
}

/** Where the screen used to go when there was no history to pop. */
export interface LiveBackFallback {
  name: string;
  params?: object;
}

export type LiveBackTarget =
  | { type: 'back' }
  | { type: 'navigate'; name: string; params?: object };

/**
 * Local `YYYY-MM-DD` for `/lives?date=`, or undefined when the session has no
 * usable start time.
 *
 * Local, not UTC: the calendar keys its days with `dateToISO(new Date(ms))`
 * (useLivesCalendar.getItemDateISO), so a UTC-derived key would open the wrong
 * day for an evening session anywhere east of Greenwich — the guest would land
 * on a calendar page that genuinely doesn't hold their session.
 */
export const livesDateParam = (scheduledAtUtc?: string | null): string | undefined => {
  if (!scheduledAtUtc) return undefined;
  const ms = new Date(scheduledAtUtc).getTime();
  if (!Number.isFinite(ms)) return undefined;
  return dateToISO(new Date(ms));
};

export const resolveLiveBackTarget = (opts: {
  canGoBack: boolean;
  isAuthenticated: boolean;
  /** The room's start, when the screen knows it — used to focus the calendar
   *  on the session's day instead of dumping the guest on today. */
  scheduledAtUtc?: string | null;
  fallback: LiveBackFallback;
}): LiveBackTarget => {
  if (opts.canGoBack) return { type: 'back' };
  if (opts.isAuthenticated) {
    return { type: 'navigate', name: opts.fallback.name, params: opts.fallback.params };
  }
  const date = livesDateParam(opts.scheduledAtUtc);
  return { type: 'navigate', name: 'Lives', params: date ? { date } : undefined };
};

/** Apply the decision above to a navigation object. */
export const goBackFromLive = (
  nav: BackNavigator,
  opts: {
    isAuthenticated: boolean;
    scheduledAtUtc?: string | null;
    fallback: LiveBackFallback;
  },
): void => {
  const target = resolveLiveBackTarget({ canGoBack: nav.canGoBack(), ...opts });
  if (target.type === 'back') nav.goBack();
  else nav.navigate(target.name, target.params);
};
